import React, { memo } from 'react';
import moment from 'moment';

import { Button, Link, Text } from 'v3/components';
import { useFleetManagement } from '../shared';
import { useSnackbar } from 'v3/components/Snackbar';

import {
  CellContainer,
  StyledCellSpan,
  StyledOccurrenceCel
} from '../styles';

import theme from 'v3/theme';

const AGGridCellRenderer = memo((props) => {

  const STATUS_COLORS = {
    1: '#FFC107', // Ordem de coleta
    2: '#FFC107', // Ag carregamento
    3: '#FFC107', // Em viagem
    4: '#2689D1', // Ag descarregamento + 3 dias
    5: '#2689D1', // Ag descarregamento
    6: '#E70000', // Carregamento finalizado
    7: '#FDE910', // Disponível
    8: '#34DB39', // Viagem externa
    9: '#FA7F72', // Ag descarregamento + 24 horas
    10: '#E70000', // Em análise
    11: '#2689D1', // Manutenção
    12: '#2689D1', // Manutenção + 2 dias

    13: '#4FC3F7', // Agenda carregamento
    14: '#29B6F6', // Agenda descarga
    15: '#9C27B0', // Sem motorista
    16: '#616161', // Indisponível
    17: '#FF9800', // Sem carreta
    18: '#8D6E63', // Sem cavalo
    19: '#00ACC1', // Descarregando
    20: '#43A047', // Carregando
  };

  const DATE_COLUMNS = [
    'availability_date',
    'availability_date_travel_external',
    'date_finish_travel',
    'date_forecast',
    'date_load_travel',
    'date_origin_travel_external',
    'date_unload_travel',
    'last_location_date',
    'next_maintenance_vehicle',
    'next_maintenance_wagon',
  ];

  const CALC_DATE_COLUMNS = [
    'time_travel_external',
    'time_maintenance',
    'time_travel',
    'time_last_travel',
    'time_available',
  ];

  const { value: renderedCellValue, colDef, data: rowData } = props;
  const columnName = colDef.field;

  const {
    outsideTravelModal,
    openOccurrencesModal,
    setSelected,
  } = useFleetManagement();

  const snackbar = useSnackbar();

  const formatDateValue = (value) => {
    if (!value) return value;

    const date = moment(
      value,
      [
        'DD/MM/YYYY HH:mm',
        'YYYY-MM-DDTHH:mm:ss.SSSZ',
        'YYYY-MM-DDTHH:mm:ssZ',
        'YYYY-MM-DDTHH:mmZ',
        'YYYY-MM-DD',
      ],
      true
    );

    return date.isValid()
      ? date.format('DD/MM/YYYY HH:mm')
      : value;
  };

  if (columnName === 'occurrences') {
    return (
      <StyledCellSpan
        onClick={() => {
          if (renderedCellValue?.travelId) {
            openOccurrencesModal(renderedCellValue?.travelId, renderedCellValue?.bennerExternalId);
          } else {
            snackbar.show(<Text>O conjunto não possui viagem em andamento</Text>, {
              type: 'error',
            });
          }
        }}
        clickable
      >
        <StyledOccurrenceCel color={theme.colors.success} title="Ocorrências concluídas">
          {renderedCellValue?.finished ?? '0'}
        </StyledOccurrenceCel>
        {' : '}
        <StyledOccurrenceCel color={theme.colors.danger} title="Ocorrências pendentes">
          {renderedCellValue?.pendency ?? '0'}
        </StyledOccurrenceCel>
      </StyledCellSpan>
    );
  }

  if (columnName === 'config_automatic_availability') {
    return (
      <CellContainer>
        <Text color="fleet_management_gray" type="regular">
          {renderedCellValue ? 'Sim' : 'Não'}
        </Text>
      </CellContainer>
    );
  }

  if (columnName === 'travel_external') {
    return (
      <CellContainer travelOutside>
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setSelected(rowData);
            outsideTravelModal.open();
          }}
        >
          Histórico de viagens
        </Button>
      </CellContainer>
    );
  }

  if (columnName === 'plate_wagons' && Array.isArray(renderedCellValue)) {
    return (
      <CellContainer $allowWrap>
        <div style={{ display: 'flex', gap: 8 }}>
          {renderedCellValue.map(({ plate, wagonId }) => (
            <Link key={wagonId} to={`/implementos/${wagonId}`} target="_blank">
              {plate}
            </Link>
          ))}
        </div>
      </CellContainer>
    );
  }

  if (columnName === 'type_wagons' || columnName === 'fleet_type_wagons') {
    return (
      <CellContainer $allowWrap>
        <Text color="fleet_management_gray" type="regular">
          {Array.isArray(renderedCellValue)
            ? renderedCellValue.map(({ name }) => name).join(', ')
            : ''}
        </Text>
      </CellContainer>
    );
  }

  if (columnName === 'body_types') {
    return (
      <CellContainer $allowWrap>
        <Text color="fleet_management_gray" type="regular">
          {renderedCellValue ? renderedCellValue?.join(', ') : 'Não informado'}
        </Text>
      </CellContainer>
    );
  }

  if (columnName === 'status_operational' && renderedCellValue?.name) {
    return (
      <CellContainer $bgColor={STATUS_COLORS[renderedCellValue.id]}>
        <Text type="regular">{renderedCellValue.name}</Text>
      </CellContainer>
    );
  }

  if (columnName === 'route_travel') {
    return (
      <CellContainer $allowWrap>
        {renderedCellValue ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Text color="fleet_management_gray" type="medium">
              {renderedCellValue.origin ? `Coleta: ${renderedCellValue.origin}` : null}
            </Text>
            <Text color="fleet_management_gray" type="medium">
              {renderedCellValue.destination ? `Entrega: ${renderedCellValue.destination}` : null}
            </Text>
          </div>
        ) : null}
      </CellContainer>
    );
  }

  if (CALC_DATE_COLUMNS.includes(columnName)) {
    return (
      <CellContainer>
        <Text color="fleet_management_gray" type="regular">
          {renderedCellValue} Dias
        </Text>
      </CellContainer>
    );
  }

  if (DATE_COLUMNS.includes(columnName)) {
    return (
      <CellContainer>
        <Text color="fleet_management_gray" type="regular">
          {formatDateValue(renderedCellValue)}
        </Text>
      </CellContainer>
    );
  }

  if (columnName === 'travel_current') {
    return (
      <CellContainer>
        {renderedCellValue && (
          <Link to={`/viagens/${renderedCellValue}`} target="_blank">
            {renderedCellValue}
          </Link>
        )}
      </CellContainer>
    );
  }

  if (columnName === 'plate') {
    return (
      <CellContainer>
        <Link to={`/veiculos/${renderedCellValue?.id}`} target="_blank">
          {renderedCellValue?.plate}
        </Link>
      </CellContainer>
    );
  }

  if (typeof renderedCellValue === 'number' || typeof renderedCellValue === 'string') {
    return (
      <CellContainer>
        <Text color="fleet_management_gray" type="regular">
          {renderedCellValue}
        </Text>
      </CellContainer>
    );
  }

  if (columnName === 'driver' && (renderedCellValue?.label || renderedCellValue?.name || renderedCellValue?.social_name || renderedCellValue?.username)) {
    return (
      <CellContainer>
        <Link to={`/motoristas/${renderedCellValue.id}`} target="_blank">
          {renderedCellValue.label || renderedCellValue.name || renderedCellValue.social_name || renderedCellValue.username}
        </Link>
      </CellContainer>
    );
  }

  if (columnName === 'shipment' && (renderedCellValue?.name || renderedCellValue?.social_name || renderedCellValue?.username)) {

    return (
      <CellContainer>
        <Link to={`/transportadoras/${renderedCellValue.id}`} target="_blank">
          {renderedCellValue.name || renderedCellValue.social_name || renderedCellValue.username}
        </Link>
      </CellContainer>
    );
  }

  if (
    renderedCellValue?.name ||
    renderedCellValue?.social_name ||
    renderedCellValue?.username ||
    renderedCellValue?.description
  ) {
    return (
      <CellContainer>
        <Text color="fleet_management_gray" type="regular">
          {
            renderedCellValue.name ||
            renderedCellValue.social_name ||
            renderedCellValue.username ||
            renderedCellValue.description
          }
        </Text>
      </CellContainer>
    );
  }

  return (
    <CellContainer>
      <Text color="fleet_management_gray" type="regular">
        {renderedCellValue}
      </Text>
    </CellContainer>
  );
});

export default AGGridCellRenderer;
